/**
 * PMGT FE-2 — Project detail page (`/projects/:projectId`).
 *
 * The detail page is now the EDIT surface of the unified project wizard: it
 * mounts the SAME `ProjectWizard` as the create page, in `mode="edit"`. The
 * old Overview | Sharing | AI-models tab layout is replaced by the stepper —
 * Basics → Parties → Parks & turbines → Overview — where the Overview step is
 * the former Overview tab (the parks→turbines tree + the status-coloured map).
 *
 * Ownership: this page finds the row in the merged own/member list to read its
 * `ownership` tell (so a MEMBER project renders read-only-style guidance — its
 * parties/grants/models are managed by the owning tenant). The BE is the
 * security boundary regardless of the UI gate.
 */
import { useMemo, type ReactElement } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { PageHeading } from "@aeriosiq/ui";
import { useOwnerMemberList } from "./access/useOwnerMemberList.js";
import { ProjectWizard } from "./wizard/ProjectWizard.js";
import { WorkflowNotSetChip } from "./WorkflowNotSetChip.js";
import type { ListProjectsParams } from "./types.js";

// A wide, unfiltered own+member sweep so the detail page can resolve the
// row's ownership/name without a single-project endpoint. Bounded by the
// member fetch cap; own paginated at a generous page size.
const LOOKUP_PARAMS: ListProjectsParams = {
  page: 1,
  pageSize: 200,
  includeCompleted: true,
  sortBy: "projectCode",
  sortDir: "desc",
};

export function ProjectDetailPage(): ReactElement {
  const { projectId = "" } = useParams<{ projectId: string }>();

  const { rows, isPending } = useOwnerMemberList(LOOKUP_PARAMS);
  const project = useMemo(
    () => rows.find((r) => r.projectId === projectId),
    [rows, projectId],
  );

  const isOwnerProject = project ? project.ownership !== "member" : true;

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: "auto",
        p: 3,
        pb: 6,
        // FB-24 — the few-px horizontal scrollbar on the edit wizard's steps
        // 1/2/3 is now fixed at the source: the live AccessGrantPanel
        // manual-grant row and the wizard's park/turbine/contract-party flex
        // rows (min-200/220/240 children) now `flexWrap` so a child that can't
        // fit a constrained track drops to a new line instead of forcing the
        // app shell's `<main>` (overflowY:auto ⇒ overflowX:auto) to show an
        // X-scrollbar. `overflowX: "clip"` is kept as a defensive backstop only
        // — it trims any residual sub-pixel overspill without creating a scroll
        // container (unlike `hidden`), so sticky/focus/scroll-anchoring is
        // unaffected. The root fixes stand on their own; this is belt-and-braces.
        overflowX: "clip",
      }}
    >
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/projects" underline="hover">
          Projects
        </Link>
        <Typography color="text.primary">
          {project ? project.projectName : "Project"}
        </Typography>
      </Breadcrumbs>

      <PageHeading>{project ? project.projectName : "Project"}</PageHeading>
      {project ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Project #{String(project.projectCode)}
          </Typography>
          {/* PM-WF-01 — the "not set" badge; the Workflow panel (Basics step)
              is where it is set. Own projects only. */}
          {isOwnerProject && project.workflow === null ? (
            <WorkflowNotSetChip />
          ) : null}
        </Stack>
      ) : null}

      {/* TT-03 — the two per-project surfaces that are NOT wizard steps: the
          standby review table and the commercial config. Both role-gate
          themselves, so the links are unconditional; a pilot following one
          gets an explanation rather than a dead end. */}
      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <Link
          component={RouterLink}
          to={`/projects/${encodeURIComponent(projectId)}/time-trackings`}
          underline="hover"
          variant="body2"
        >
          Time trackings
        </Link>
        <Link
          component={RouterLink}
          to={`/projects/${encodeURIComponent(projectId)}/settings`}
          underline="hover"
          variant="body2"
        >
          Project settings
        </Link>
      </Stack>

      {isPending && !project ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3} sx={{ mt: 2 }}>
          {!isOwnerProject ? (
            <Alert severity="info" variant="outlined">
              This project is shared with you by{" "}
              {project?.ownerTenantName ?? "another company"}. Its access,
              parties, and models are managed by the owning company.
            </Alert>
          ) : null}

          <ProjectWizard
            mode="edit"
            projectId={projectId}
            isOwnerProject={isOwnerProject}
            // DL-01 — the caller's role on a member project (customer download)
            accessRole={project?.role ?? null}
          />
        </Stack>
      )}
    </Box>
  );
}
